import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { fetchCandidate, updateCandidate } from '@/lib/api';
import { normalizeOptionalUrlInput } from '@/lib/url';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SkillsMultiSelect } from '@/components/ui/skills-multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const JOB_TYPE_OPTIONS = ['Full Time', 'Part Time', 'Contract', 'Internship'] as const;
const SOURCE_OPTIONS = ['manual', 'referral', 'linkedin', 'job_board', 'website', 'whatsapp'] as const;

const candidateSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  whatsappPhone: z.string().min(10, 'Valid phone number is required').max(20),
  email: z.string().email('Invalid email').max(255).optional().or(z.literal('')),
  phoneSecondary: z.string().max(20).optional(),
  currentLocation: z.string().max(255).optional(),
  nationality: z.string().max(100).optional(),
  yearsExperience: z.coerce.number().min(0).optional().or(z.literal('')),
  currentJobTitle: z.string().max(255).optional(),
  currentEmployer: z.string().max(255).optional(),
  expectedSalaryRange: z.string().max(100).optional(),
  availabilityDate: z.string().optional(),
  linkedinUrl: z.preprocess(
    normalizeOptionalUrlInput,
    z.string().url('Enter a valid LinkedIn URL').max(500).optional(),
  ),
  source: z.string().max(100).optional(),
});

type CandidateFormData = z.infer<typeof candidateSchema>;

function EditCandidateSkeleton() {
  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <Skeleton className="h-6 w-32" />
      <div className="space-y-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-5 w-96" />
      </div>
      <Skeleton className="h-64 w-full" />
      <Skeleton className="h-48 w-full" />
    </div>
  );
}

export function EditCandidatePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [skills, setSkills] = useState<string[]>([]);
  const [preferredJobTypes, setPreferredJobTypes] = useState<string[]>([]);

  const {
    data: candidate,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['candidate', id],
    queryFn: () => fetchCandidate(id!),
    enabled: !!id,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CandidateFormData>({
    resolver: zodResolver(candidateSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      whatsappPhone: '',
      email: '',
      phoneSecondary: '',
      currentLocation: '',
      nationality: '',
      currentJobTitle: '',
      currentEmployer: '',
      expectedSalaryRange: '',
      availabilityDate: '',
      linkedinUrl: '',
      source: 'manual',
    },
  });

  // Radix Select fields are not native inputs, so register them explicitly.
  useEffect(() => {
    register('source');
  }, [register]);

  useEffect(() => {
    if (candidate) {
      reset({
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        whatsappPhone: candidate.whatsappPhone,
        email: candidate.email || '',
        phoneSecondary: candidate.phoneSecondary || '',
        currentLocation: candidate.currentLocation || '',
        nationality: candidate.nationality || '',
        yearsExperience: candidate.yearsExperience ?? '',
        currentJobTitle: candidate.currentJobTitle || '',
        currentEmployer: candidate.currentEmployer || '',
        expectedSalaryRange: candidate.expectedSalaryRange || '',
        availabilityDate: candidate.availabilityDate
          ? candidate.availabilityDate.split('T')[0]
          : '',
        linkedinUrl: candidate.linkedinUrl || '',
        source: candidate.source || 'manual',
      });
      // Force custom Select controls to sync after reset; Radix Select is not a native input.
      setValue('source', candidate.source || 'manual', { shouldValidate: true });
      setSkills(candidate.skills || []);
      setPreferredJobTypes(candidate.preferredJobTypes || []);
    }
  }, [candidate, reset, setValue]);

  const updateMutation = useMutation({
    mutationFn: (data: Parameters<typeof updateCandidate>[1]) => updateCandidate(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidate', id] });
      queryClient.invalidateQueries({ queryKey: ['candidates'] });
      navigate(`/candidates/${id}`);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to update candidate');
    },
  });

  const onSubmit = (data: CandidateFormData) => {
    setError(null);
    updateMutation.mutate({
      firstName: data.firstName,
      lastName: data.lastName,
      whatsappPhone: data.whatsappPhone,
      email: data.email || undefined,
      phoneSecondary: data.phoneSecondary || undefined,
      currentLocation: data.currentLocation || undefined,
      nationality: data.nationality || undefined,
      yearsExperience: data.yearsExperience === '' ? undefined : data.yearsExperience as number | undefined,
      currentJobTitle: data.currentJobTitle || undefined,
      currentEmployer: data.currentEmployer || undefined,
      skills,
      preferredJobTypes,
      expectedSalaryRange: data.expectedSalaryRange || undefined,
      availabilityDate: data.availabilityDate || undefined,
      linkedinUrl: data.linkedinUrl || undefined,
      source: data.source || undefined,
    });
  };

  const toggleJobType = (type: string) => {
    setPreferredJobTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  if (isLoading) {
    return <EditCandidateSkeleton />;
  }

  if (fetchError || !candidate) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-text-primary">Candidate Not Found</h2>
          <p className="text-text-tertiary mt-2">
            The candidate you are trying to edit does not exist or has been removed.
          </p>
          <Button asChild className="mt-4">
            <Link to="/candidates">Back to Candidates</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div>
        <Link
          to={`/candidates/${id}`}
          className="inline-flex items-center gap-2 text-sm text-text-tertiary hover:text-text-primary"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Candidate
        </Link>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-text-primary">Edit Candidate</h1>
        <p className="text-text-secondary mt-1">
          Update details for "{candidate.firstName} {candidate.lastName}"
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit, () => setError('Please check the form for errors before saving.'))} className="space-y-6">
        {error && (
          <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-md">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Personal Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input id="firstName" {...register('firstName')} />
                {errors.firstName && (
                  <p className="text-sm text-red-600">{errors.firstName.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input id="lastName" {...register('lastName')} />
                {errors.lastName && (
                  <p className="text-sm text-red-600">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="whatsappPhone">WhatsApp Phone *</Label>
                <Input id="whatsappPhone" {...register('whatsappPhone')} />
                {errors.whatsappPhone && (
                  <p className="text-sm text-red-600">{errors.whatsappPhone.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" {...register('email')} />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phoneSecondary">Secondary Phone</Label>
                <Input id="phoneSecondary" {...register('phoneSecondary')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nationality">Nationality</Label>
                <Input id="nationality" {...register('nationality')} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currentLocation">Current Location</Label>
                <Input id="currentLocation" {...register('currentLocation')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="linkedinUrl">LinkedIn URL</Label>
                <Input
                  id="linkedinUrl"
                  type="url"
                  placeholder="linkedin.com/in/..."
                  {...register('linkedinUrl')}
                />
                {errors.linkedinUrl && (
                  <p className="text-sm text-red-600">{errors.linkedinUrl.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Professional Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Professional Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currentJobTitle">Current Job Title</Label>
                <Input id="currentJobTitle" {...register('currentJobTitle')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="currentEmployer">Current Employer</Label>
                <Input id="currentEmployer" {...register('currentEmployer')} />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="yearsExperience">Years of Experience</Label>
              <Input
                id="yearsExperience"
                type="number"
                min="0"
                {...register('yearsExperience')}
              />
            </div>

            <div className="space-y-2">
              <Label>Skills</Label>
              <SkillsMultiSelect skills={skills} onSkillsChange={setSkills} />
            </div>
          </CardContent>
        </Card>

        {/* Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Preferences</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Preferred Job Types</Label>
              <div className="flex flex-wrap gap-2">
                {JOB_TYPE_OPTIONS.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleJobType(type)}
                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
                      preferredJobTypes.includes(type)
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400'
                        : 'border-white/10 text-text-secondary hover:border-white/20'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="expectedSalaryRange">Expected Salary Range</Label>
                <Input
                  id="expectedSalaryRange"
                  {...register('expectedSalaryRange')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="availabilityDate">Availability Date</Label>
                <Input
                  id="availabilityDate"
                  type="date"
                  {...register('availabilityDate')}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Additional</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="source">Source</Label>
              <Select
                value={watch('source') || 'manual'}
                onValueChange={(value) => setValue('source', value, { shouldDirty: true, shouldValidate: true })}
              >
                <SelectTrigger id="source">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((src) => (
                    <SelectItem key={src} value={src}>
                      {src.charAt(0).toUpperCase() + src.slice(1).replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" asChild>
            <Link to={`/candidates/${id}`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
